import { ObjectId } from 'bson';

function updateCalculatedColumns(calcCols, replacements) {
	// 1) Snapshot originals
	const originals = (Array.isArray(calcCols) ? calcCols : []).map((col) => ({
		originalField: col.field,
		originalFields: Array.isArray(col.fields) ? col.fields.slice() : [],
		originalExpr: typeof col.expression === 'string' ? col.expression : '',
	}));

	// 2) Build initial drop set = all keys in replacements mapped to null
	const dropSet = new Set(
		Object.entries(replacements || {})
			.filter(([_, val]) => val === null)
			.map(([key]) => key),
	);

	// 3) Cascade‑drop any calc that refers to a dropped field
	let changed;
	do {
		changed = false;
		for (const col of originals) {
			if (!dropSet.has(col.originalField) && col.originalFields.some((f) => dropSet.has(f))) {
				dropSet.add(col.originalField);
				changed = true;
			}
		}
	} while (changed);

	// 4) Identify removed columns
	const removed = originals.filter((col) => dropSet.has(col.originalField)).map((col) => col.originalField);

	// 5) Keep the rest
	const kept = originals.filter((col) => !dropSet.has(col.originalField));

	// 6) Helper to escape regex metachars
	const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

	// 7) Build updated columns with renames applied
	const updated = kept.map((col) => {
		// rename the calc column itself if it’s in replacements
		const newField = replacements[col.originalField] != null ? replacements[col.originalField] : col.originalField;

		// rename each source‑field
		const newFields = col.originalFields.map((f) => (replacements[f] != null ? replacements[f] : f));

		// apply all non-null renames into the expression
		let newExpr = col.originalExpr;
		for (const [oldKey, newVal] of Object.entries(replacements)) {
			if (newVal != null) {
				const re = new RegExp(`\\b${escapeRe(oldKey)}\\b`, 'g');
				newExpr = newExpr.replace(re, newVal);
			}
		}

		return {
			field: newField,
			fields: newFields,
			expression: newExpr,
		};
	});

	// 8) Always return arrays, even if empty
	return {
		updated: Array.isArray(updated) ? updated : [],
		removed: Array.isArray(removed) ? removed : [],
	};
}

const replaceDatasetInDataSpace = (dataSpace, oldDatasetInstanceId, newDatasetInstance) => {
	const updatedDataSpace = {};

	// Handle the DataSpace _id
	updatedDataSpace._id = dataSpace._id;

	// Handle the DataSpace creationDate
	updatedDataSpace.creationDate = dataSpace.creationDate;

	// Handle the DataSpace lastRefreshedDate
	updatedDataSpace.lastRefreshedDate = dataSpace.lastRefreshedDate;

	// Handle the DataSpace name
	updatedDataSpace.name = dataSpace.name;

	// Handle the DataSpace createdBy
	updatedDataSpace.createdBy = dataSpace.createdBy;

	// Handle the DataSpace isPublic
	updatedDataSpace.isPublic = dataSpace.isPublic;

	// Handle the DataSpace isCurrentViewSaved
	updatedDataSpace.isCurrentViewSaved = dataSpace.isCurrentViewSaved;

	// Handle the DataSpace isFeatured
	updatedDataSpace.isFeatured = dataSpace.isFeatured;

	const oldDatasetInstance = dataSpace.datasets.find((dataset) => dataset._id === oldDatasetInstanceId);

	const columnReplacementsObject = {};

	for (let field of oldDatasetInstance.fields) {
		const newField = newDatasetInstance.fields.find(
			(newField) => newField.name.split('__')[0] === field.name.split('__')[0],
		);
		columnReplacementsObject[field.name] = newField?.name || null;
	}

	// calculatedColumns: Replace any references to columns from the old dataset and remove any calculated columns that reference columns that do not exist in the new dataset
	// Also remove calculated columns that refer to calculated columns that were removed
	const { updated: updatedCalculatedColumns, removed: removedCalculatedColumns } = updateCalculatedColumns(
		dataSpace.calculatedColumns || [],
		columnReplacementsObject,
	);

	// Update the calculated columns in the dataSpace
	updatedDataSpace.calculatedColumns = updatedCalculatedColumns;

	// Add the removed calculated columns to the columnReplacementsObject as null values
	for (let removedCalculatedColumn of removedCalculatedColumns) {
		columnReplacementsObject[removedCalculatedColumn] = null;
	}

	// headerNamesState: If a column is removed that is referenced in headerNamesState, we remove that header
	updatedDataSpace.headerNamesState = dataSpace.headerNamesState.reduce((acc, header) => {
		if (header.field in columnReplacementsObject) {
			if (columnReplacementsObject[header.field] === null) {
				return acc;
			}
			acc.push({
				...header,
				field: columnReplacementsObject[header.field],
			});
			return acc;
		}

		acc.push(header);
		return acc;
	}, []);

	// datasets: We replace the old dataset with the new dataset
	updatedDataSpace.datasets = dataSpace.datasets.map((dataset) =>
		dataset._id === oldDatasetInstanceId ? newDatasetInstance : dataset,
	);

	// frozenColumns: Remove any frozen columns that reference columns that are removed
	updatedDataSpace.frozenColumns = dataSpace.frozenColumns.reduce((acc, frozenColumn) => {
		if (columnReplacementsObject[frozenColumn] === null) {
			return acc;
		}
		acc.push(columnReplacementsObject[frozenColumn] || frozenColumn);
		return acc;
	}, []);

	// toggledColumnsState: Remove any toggled columns that reference columns that are removed
	updatedDataSpace.toggledColumnsState = dataSpace.toggledColumnsState.reduce((acc, toggledColumn) => {
		if (columnReplacementsObject[toggledColumn] === null) {
			return acc;
		}
		acc.push(columnReplacementsObject[toggledColumn] || toggledColumn);
		return acc;
	}, []);

	// columnMetadata: Remove any columns that were removed and add the new dataset columns
	updatedDataSpace.columnMetadata = dataSpace.columnMetadata.filter(
		(column) => !(column.field in columnReplacementsObject),
	);
	// Add new dataset columns
	for (let column of newDatasetInstance.fields) {
		const newDatasetInstanceColumn = {
			_id: new ObjectId().toString(),
			field: column.name,
			header: column.displayName,
			dataSourceName: newDatasetInstance.name,
			description: column.description,
		};

		const header = updatedDataSpace.headerNamesState.find((header) => header.field === column.name);
		if (header) {
			newDatasetInstanceColumn.header = header.header;
		}
		updatedDataSpace.columnMetadata.push(newDatasetInstanceColumn);
	}

	// joinData: Replace the old dataset with the new one and remove any joins that reference columns that are no longer exist in the new dataset
	const updatedJoinData = [];
	for (let joinsDataset of dataSpace.joinData) {
		let updatedDatasetJoins = [];
		// Populate updatedDatasetJoins with the updated joins of the dataset
		for (let join of joinsDataset.joins) {
			// If one of the join fields doesn't exist in the new dataset, remove the join
			if (
				columnReplacementsObject[join.primaryDataset.joinFieldName] === null ||
				columnReplacementsObject[join.foreignDataset.joinFieldName] === null
			) {
				continue;
			}
			// If the primary dataset is the old dataset, update the primary dataset
			if (join.primaryDataset._id === oldDatasetInstanceId) {
				const newDatasetJoinFieldColumn = newDatasetInstance.fields.find(
					(field) => field.name === columnReplacementsObject[join.primaryDataset.joinFieldName],
				);
				// If the join is on the old dataset, we need to update the join
				updatedDatasetJoins.push({
					...join,
					primaryDataset: {
						_id: newDatasetInstance._id,
						name: newDatasetInstance.name,
						displayName: newDatasetInstance.displayName,
						joinFieldName: columnReplacementsObject[join.primaryDataset.joinFieldName],
						joinFieldDisplayName: newDatasetJoinFieldColumn.displayName || newDatasetJoinFieldColumn.name,
					},
				});
				continue;
			}
			// If the foreign dataset is the old dataset, update the foreign dataset
			if (join.foreignDataset._id === oldDatasetInstanceId) {
				const newDatasetJoinFieldColumn = newDatasetInstance.fields.find(
					(field) => field.name === columnReplacementsObject[join.foreignDataset.joinFieldName],
				);
				// If the join is on the old dataset, we need to update the join
				updatedDatasetJoins.push({
					...join,
					foreignDataset: {
						_id: newDatasetInstance._id,
						name: newDatasetInstance.name,
						displayName: newDatasetInstance.displayName,
						joinFieldName: columnReplacementsObject[join.foreignDataset.joinFieldName],
						joinFieldDisplayName: newDatasetJoinFieldColumn.displayName || newDatasetJoinFieldColumn.name,
					},
				});
				continue;
			}
			// Otherwise, we just push the join
			updatedDatasetJoins.push(join);
		}

		if (joinsDataset._id === oldDatasetInstanceId) {
			const updatedJoinDataset = {
				...newDatasetInstance,
				isAnchor: joinsDataset.isAnchor,
				joins: updatedDatasetJoins,
			};
			delete updatedJoinDataset.fields;
			updatedJoinData.push(updatedJoinDataset);
		} else {
			updatedJoinData.push({
				...joinsDataset,
				joins: updatedDatasetJoins,
			});
		}
	}

	updatedDataSpace.joinData = updatedJoinData;

	// views: Replace old dataset columns with new dataset columns
	// Also remove any views that reference columns from the old dataset that do not exist in the new dataset
	const updatedViews = [];
	for (let view of dataSpace.views) {
		const updatedInternalPropertiesState = {
			d_rows: view.internalProperties.state.d_rows,
			editingCells: view.internalProperties.state.editingCells,
			first: view.internalProperties.state.first,
			rows: view.internalProperties.state.rows,
			sortOrder: view.internalProperties.state.sortOrder,
		};
		// Update the internal state sort field
		if ('sortField' in view.internalProperties.state) {
			// If the replacement column doesn't exist for the sort field, skip the entire view.
			if (columnReplacementsObject[view.internalProperties.state.sortField] === null) {
				continue;
			}
			// Otherwise, update the sort field.
			updatedInternalPropertiesState.sortField =
				columnReplacementsObject[view.internalProperties.state.sortField] || view.internalProperties.state.sortField;
		}
		// Update the internal state multiSortMeta field
		if ('multiSortMeta' in view.internalProperties.state) {
			const updatedMultiSortMeta = [];
			for (let sort of view.internalProperties.state.multiSortMeta) {
				if (columnReplacementsObject[sort.field] === null) {
					continue;
				}
				updatedMultiSortMeta.push({
					field: columnReplacementsObject[sort.field] || sort.field,
					order: sort.order,
				});
			}
			// If a sort was removed, skip the entire view
			if (updatedMultiSortMeta.length !== view.internalProperties.state.multiSortMeta.length) {
				continue;
			}
			updatedInternalPropertiesState.multiSortMeta = updatedMultiSortMeta;
		}
		// Update the internal state filters field
		if (view.internalProperties.state.filters) {
			const updatedFilters = {};
			for (let columnName in view.internalProperties.state.filters) {
				if (columnReplacementsObject[columnName] === null) {
					continue;
				}
				updatedFilters[columnReplacementsObject[columnName] || columnName] =
					view.internalProperties.state.filters[columnName];
			}
			// If a filter was removed, skip the entire view
			if (Object.keys(updatedFilters).length !== Object.keys(view.internalProperties.state.filters || {}).length) {
				continue;
			}
			updatedInternalPropertiesState.filters = updatedFilters;
		}
		// Update the internal state columnOrder field
		if ('columnOrder' in view.internalProperties.state) {
			const updatedColumnOrder = [];
			for (let column of view.internalProperties.state.columnOrder) {
				if (columnReplacementsObject[column] === null) {
					continue;
				}
				updatedColumnOrder.push(columnReplacementsObject[column] || column);
			}
			// If a column was removed, skip the entire view
			if (updatedColumnOrder.length !== view.internalProperties.state.columnOrder.length) {
				continue;
			}
			updatedInternalPropertiesState.columnOrder = updatedColumnOrder;
		}

		const updatedExternalProperties = {};
		// Update the externalProperties selectedColumns
		if ('selectedColumns' in view.externalProperties) {
			const updatedSelectedColumns = [];
			for (let selectedColumn of view.externalProperties.selectedColumns) {
				if (columnReplacementsObject[selectedColumn.field] === null) {
					continue;
				}
				const updatedSelectedColumnName = columnReplacementsObject[selectedColumn.field] || selectedColumn.field;

				const columnMetadataColumn = updatedDataSpace.columnMetadata.find(
					(column) => column.field === updatedSelectedColumnName,
				);

				if (columnMetadataColumn) {
					updatedSelectedColumns.push({
						_id: String(columnMetadataColumn._id),
						field: updatedSelectedColumnName,
						header: columnMetadataColumn.header,
						dataSourceName: columnMetadataColumn.dataSourceName,
						description: columnMetadataColumn.description,
						columnToggleLabel: columnMetadataColumn.header + ' - ' + columnMetadataColumn.dataSourceName,
					});
				}
			}
			// If a column was removed, skip the entire view
			if (updatedSelectedColumns.length !== view.externalProperties.selectedColumns.length) {
				continue;
			}
			updatedExternalProperties.selectedColumns = updatedSelectedColumns;
		}
		// Update the externalProperties filterStates
		if ('filterStates' in view.externalProperties) {
			const updatedFilterStates = {};
			for (let columnName in view.externalProperties.filterStates) {
				if (columnReplacementsObject[columnName] === null) {
					continue;
				}
				updatedFilterStates[columnReplacementsObject[columnName] || columnName] =
					view.externalProperties.filterStates[columnName];
			}
			// If a filter was removed, skip the entire view
			if (Object.keys(updatedFilterStates).length !== Object.keys(view.externalProperties.filterStates).length) {
				continue;
			}
			updatedExternalProperties.filterStates = updatedFilterStates;
		}
		// Update the externalProperties frozenColumns
		if ('frozenColumns' in view.externalProperties) {
			const updatedFrozenColumns = [];
			for (let frozenColumn of view.externalProperties.frozenColumns) {
				if (columnReplacementsObject[frozenColumn] === null) {
					continue;
				}
				updatedFrozenColumns.push(columnReplacementsObject[frozenColumn] || frozenColumn);
			}
			// If a column was removed, skip the entire view
			if (updatedFrozenColumns.length !== view.externalProperties.frozenColumns.length) {
				continue;
			}
			updatedExternalProperties.frozenColumns = updatedFrozenColumns;
		}

		// Add the view to the updated views
		updatedViews.push({
			id: view.id,
			name: view.name,
			internalProperties: {
				state: updatedInternalPropertiesState,
			},
			externalProperties: updatedExternalProperties,
		});
	}

	updatedDataSpace.views = updatedViews;

	// internalState: Set to an empty object
	updatedDataSpace.internalState = {};

	return updatedDataSpace;
};

export default replaceDatasetInDataSpace;
